import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Card, CardContent, Typography, Box, Grid, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
  Button, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { Assessment, Refresh } from '@mui/icons-material';
import { useAuth } from '../App';
import axios from 'axios';

const API_BASE = API_BASE_URL;
const USC_GREEN = '#046A38';

const ACTION_META = {
  approve: { label: 'Approval', color: 'success' },
  deny: { label: 'Denial', color: 'error' },
  edit: { label: 'Edit', color: 'info' },
  delete: { label: 'Delete', color: 'warning' },
};

const AdminActivityLogPage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [byAction, setByAction] = useState({});
  const [byActor, setByActor] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    if (user?.idToken) loadLog();
  }, [user?.idToken]);

  const loadLog = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/admin/activity-log`, {
        headers: { Authorization: `Bearer ${user.idToken}` },
      });
      setLogs(res.data.logs || []);
      setByAction(res.data.byAction || {});
      setByActor(res.data.byActor || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const filteredLogs = useMemo(
    () => (actionFilter === 'all' ? logs : logs.filter((l) => l.action === actionFilter)),
    [logs, actionFilter]
  );

  const StatCard = ({ label, value, color }) => (
    <Card elevation={2}>
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: color || USC_GREEN }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </CardContent>
    </Card>
  );

  const topActors = Object.entries(byActor).sort((a, b) => b[1] - a[1]).slice(0, 6);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: USC_GREEN, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment fontSize="large" /> Activity Log
        </Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadLog} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stat cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={2.4}><StatCard label="Total actions" value={logs.length} /></Grid>
        <Grid item xs={6} sm={2.4}><StatCard label="Approvals" value={byAction.approve || 0} color="#2e7d32" /></Grid>
        <Grid item xs={6} sm={2.4}><StatCard label="Denials" value={byAction.deny || 0} color="#c62828" /></Grid>
        <Grid item xs={6} sm={2.4}><StatCard label="Edits" value={byAction.edit || 0} color="#0277bd" /></Grid>
        <Grid item xs={6} sm={2.4}><StatCard label="Deletes" value={byAction.delete || 0} color="#e65100" /></Grid>
      </Grid>

      {/* By admin */}
      {topActors.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>By admin</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {topActors.map(([actor, count]) => (
                <Chip key={actor} label={`${actor}: ${count}`} variant="outlined" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Log table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Recent activity</Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Filter</InputLabel>
              <Select value={actionFilter} label="Filter" onChange={(e) => setActionFilter(e.target.value)}>
                <MenuItem value="all">All actions</MenuItem>
                <MenuItem value="approve">Approvals</MenuItem>
                <MenuItem value="deny">Denials</MenuItem>
                <MenuItem value="edit">Edits</MenuItem>
                <MenuItem value="delete">Deletes</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : filteredLogs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No activity recorded yet.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>When</strong></TableCell>
                    <TableCell><strong>Action</strong></TableCell>
                    <TableCell><strong>Who (person acted on)</strong></TableCell>
                    <TableCell><strong>By admin</strong></TableCell>
                    <TableCell><strong>Details</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLogs.map((log) => {
                    const meta = ACTION_META[log.action] || { label: log.action, color: 'default' };
                    return (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(log.timestamp)}</TableCell>
                        <TableCell><Chip label={meta.label} size="small" color={meta.color} /></TableCell>
                        <TableCell>
                          {log.targetName || '—'}
                          {log.targetEmail ? <><br /><Typography variant="caption" color="text.secondary">{log.targetEmail}</Typography></> : null}
                        </TableCell>
                        <TableCell>{log.actor}</TableCell>
                        <TableCell>{log.details}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default AdminActivityLogPage;
